import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const AnimatedSection = ({ children, animationVariants, className, triggerOnce, staggerChildren, tag = "div" }) => {
    const MotionComponent = motion[tag];
    return (
        <MotionComponent
            variants={animationVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: triggerOnce !== false }}
            className={className}
            transition={staggerChildren ? { staggerChildren } : undefined}
        >
            {children}
        </MotionComponent>
    );
};

const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};
// --- End AnimatedSection ---

// --- Data is now complete for all four tabs ---
const faqData = [
  {
    id: 'bloxblueprint',
    title: 'BloxBluePrint Modeler',
    faqs: [
      {
        question: 'What is BloxBluePrint Modeler?',
        answer: 'BloxBluePrint Modeler is a Blockchain-Based Business Process Management System (BPMS). It offers a decentralized, transparent, and tamper-proof way to execute your business processes by using blockchain technology as a core part of its environment.',
      },
      {
        question: 'How is this different from traditional process management systems?',
        answer: 'Traditional systems often operate in centralized environments, which can lead to challenges. They can be complex, inefficient, and lack transparency, especially when multiple organizations are involved. Traditional systems also struggle with trust between parties, as there is often no single source of truth. BloxBluePrint Modeler addresses these issues by using blockchain\'s decentralized nature to create a shared, trusted environment for all participants.',
      },
      {
        question: 'Why should my business use a blockchain-based system like this?',
        answer: (
          <>
            <p className="mb-4">Using a blockchain-based BPMS offers several key advantages over traditional systems:</p>
            <ul className="space-y-3 list-disc pl-5">
              <li><strong>Increased Efficiency and Reduced Costs:</strong> It automates and streamlines business processes using smart contracts, which reduces the need for intermediaries and lowers transaction costs. It also eliminates the need for manual reconciliations, which cuts down on administrative overhead.</li>
              <li><strong>Enhanced Transparency and Traceability:</strong> Every transaction is recorded on a shared ledger, providing full transparency and making it easy to track and audit all activities. This builds an immutable and auditable record of your business processes, which reduces fraud and disputes.</li>
              <li><strong>Improved Security:</strong> The cryptographic features of blockchain ensure that all transactions are secure and resistant to tampering, which significantly reduces the risk of fraud and cyber-attacks.</li>
              <li><strong>Faster Transactions:</strong> By eliminating the need to go through intermediate entities, transactions become faster and more efficient, which is especially beneficial for cross-border payments.</li>
              <li><strong>Enables Trust for Collaboration:</strong> It facilitates collaboration between different organizations without needing a trusted central party. This solves the common problem where companies cannot agree on a single provider to control a shared process.</li>
            </ul>
          </>
        ),
      },
      {
        question: 'What core features does the BloxBluePrint Modeler offer?',
        answer: (
            <>
                <p className="mb-4">The platform is designed to provide the essential features you expect from a top-tier BPMS, including:</p>
                <ul className="space-y-3 list-disc pl-5">
                    <li><strong>Workflow Automation:</strong> The ability to automate your business's workflows and processes, such as approving expense reports or scheduling meetings.</li>
                    <li><strong>Process Management:</strong> It helps you manage your business processes, from defining them to tracking their progress.</li>
                    <li><strong>Dashboards:</strong> It provides visual dashboards that give you at-a-glance access to crucial, real-time information about how your business is performing.</li>
                    <li><strong>Analytics Tools:</strong> It includes built-in analytics tools to track data from across your organization in one central place, helping you make better strategic decisions.</li>
                    <li><strong>Integration Capabilities:</strong> The platform is capable of integrating with other key business systems, such as HR, CRM, and ERP platforms.</li>
                </ul>
            </>
        ),
      },
      {
        question: 'How will my team interact with the platform?',
        answer: 'The system will provide a simple user interface for your team to interact with. Since many processes happen on the blockchain, the platform will be sure to alert users of their required involvement, either through the interface or via notifications like email.',
      },
    ],
  },
  {
    id: 'bpsc',
    title: 'BPSC Compiler',
    faqs: [
        {
            question: 'What is the BPSC Compiler and how does it help my business?',
            answer: 'The Business Process to Smart Contract (BPSC) Compiler is a powerful tool that automatically transforms your existing business process diagrams into secure, blockchain-based smart contracts. This helps you streamline blockchain adoption, reduce operational costs by automating complex workflows, and get your blockchain projects to market faster.'
        },
        {
            question: 'How does the BPSC Compiler work?',
            answer: 'You provide a standard business process diagram (specifically, a BPMN 2.0 model), and the compiler automatically translates it into an executable smart contract. It embeds your specific business rules directly into the blockchain workflow, ensuring both accuracy and regulatory compliance. This enables seamless automation of even complex processes.'
        },
        {
            question: 'What are the key benefits of using the BPSC Compiler?',
            answer: (
                <>
                    <p className="mb-4">The compiler offers several significant business advantages:</p>
                    <ul className="space-y-3 list-disc pl-5">
                        <li><strong>Reduced Costs:</strong> It helps lower operational costs by automating complex business workflows.</li>
                        <li><strong>Faster Deployment:</strong> You can significantly accelerate the time-to-market for your blockchain projects.</li>
                        <li><strong>Lower Risk:</strong> It helps mitigate compliance risks by building automated regulatory checks directly into the smart contract.</li>
                        <li><strong>Greater Trust:</strong> It enhances transparency and trust among all parties by using immutable, blockchain-based processes.</li>
                        <li><strong>Simplified Development:</strong> It simplifies the creation of smart contracts, even for teams that have limited blockchain expertise.</li>
                    </ul>
                </>
            )
        },
        {
            question: 'How does the compiler handle compliance and regulations?',
            answer: 'The compiler has a built-in compliance engine that embeds jurisdiction-specific regulatory requirements (like AML and KYC) directly into the smart contracts. This automates compliance checks as part of the workflow. It can also allow for real-time updates to the smart contracts if regulatory standards change.'
        },
        {
            question: 'Can I customize the smart contracts for my specific needs?',
            answer: 'Yes. The compiler is designed for extensibility and customization. It provides pre-built modules for common scenarios like asset tokenization and cross-border payments. You can also define custom rules and advanced logic through a scripting interface.'
        },
        {
            question: 'What kind of business processes can I automate with the compiler?',
            answer: (
                <>
                    <p className="mb-4">You can automate a wide range of financial and business workflows. Common examples include:</p>
                    <ul className="space-y-3 list-disc pl-5">
                        <li><strong>Automated Cross-Border Payments:</strong> The compiler can create a smart contract that handles automatic fund transfers, exchange rate calculations, and KYC validation.</li>
                        <li><strong>Asset Tokenization:</strong> You can design a process for issuing and managing assets, and the compiler will generate a smart contract to handle token issuance, transfer rules, and redemptions.</li>
                        <li><strong>Loan Processing:</strong> It can turn a loan approval workflow into a smart contract that automates credit checks, collateral verification, fund disbursement, and repayment schedules.</li>
                    </ul>
                </>
            )
        },
        {
            question: 'How does this tool ensure security and reliability?',
            answer: (
                <>
                    <p className="mb-4">The BPSC Compiler ensures security and reliability in several ways:</p>
                    <ul className="space-y-3 list-disc pl-5">
                        <li><strong>Transaction Integrity:</strong> It guarantees that all workflows are executed exactly as defined, with no unauthorized changes.</li>
                        <li><strong>Audit Trails:</strong> It automatically maintains a secure and unchangeable log of all actions for easy auditing.</li>
                        <li><strong>Fault Tolerance:</strong> It includes features to automatically retry failed transactions or roll back to a safe state to ensure your workflows continue without disruption.</li>
                    </ul>
                </>
            )
        }
    ],
  },
  {
    id: 'frontierblox',
    title: 'FrontierBlox Engine',
    faqs: [
      {
        question: 'What is the FrontierBlox Engine and how does it help my business?',
        answer: 'The FrontierBlox Engine is a core part of the ZenithBlox platform that serves as a robust compliance and security layer for blockchain transactions. It is designed to enhance security, ensure compliance, and provide real-time monitoring. This enables your institution to confidently adopt blockchain technology while reducing risks and meeting regulatory standards. The main purpose is to ensure all blockchain transactions are secure, compliant, and efficient.',
      },
      {
        question: 'What are the main features of FrontierBlox?',
        answer: (
          <>
            <p className="mb-4">FrontierBlox provides two main features to help you manage your application:</p>
            <ul className="space-y-3 list-disc pl-5">
              <li><strong>The Designer:</strong> This is where you will define the rules and policies you wish to apply to your smart contract.</li>
              <li><strong>The Controller:</strong> This feature acts as an oracle, making decisions and enforcing the rules you set in the Designer.</li>
            </ul>
          </>
        ),
      },
      {
        question: 'How do I create and manage my transaction rules?',
        answer: "You can easily set up your rules using the Designer's user-friendly interface. Simply upload your smart contract and define the policies you want to apply. These rules are then automatically injected within your smart contract. The Designer also generates special \"FrontierBlox\" smart contracts that represent your rules.",
      },
      {
        question: 'What kinds of rules can I apply to my application?',
        answer: (
          <>
            <p className="mb-4">You can apply several powerful, predefined rules to enhance your application's security and compliance:</p>
            <ul className="space-y-3 list-disc pl-5">
              <li><strong>Know Your Customer (KYC):</strong> Manage who can access your application through a whitelist, helping you meet compliance standards.</li>
              <li><strong>Restrict Forbidden Accounts:</strong> Prevent a list of forbidden accounts on the Ethereum blockchain from accessing your application.</li>
              <li><strong>Limit Transaction Amounts:</strong> Set a maximum transfer amount for transactions to control financial flows within your application.</li>
              <li><strong>Enforce Geographic Restrictions:</strong> Use this tool to enforce location-based restrictions for users of your decentralized application.</li>
            </ul>
          </>
        ),
      },
      {
        question: 'How does FrontierBlox enforce my rules during transactions?',
        answer: 'The Controller feature enforces your rules by connecting to external data sources to make decisions. It automatically validates that every transaction complies with the rules you have set. The Controller also monitors and records the transaction history in logs, giving you a clear overview for audits and oversight.',
      },
      {
        question: "How can I monitor my application's activity?",
        answer: (
            <>
                <p className="mb-4">You can gain valuable insights into your application's performance by using integrated monitoring tools.</p>
                <ul className="space-y-3 list-disc pl-5">
                    <li><strong>Dune Analytics</strong> is an analytics platform that lets you create and share interactive dashboards to get visual insights into on-chain data.</li>
                    <li><strong>The Graph</strong> is a decentralized protocol that provides rapid and structured access to your smart contract's events and states for efficient monitoring.</li>
                </ul>
            </>
        )
      },
      {
        question: 'Which monitoring tool is right for me?',
        answer: (
            <>
                <p className="mb-4">Your choice depends on your goals:</p>
                <ul className="space-y-3 list-disc pl-5">
                    <li>Use <strong>Dune Analytics</strong> for creating custom, interactive dashboards to analyze trends and visualize network activity. It is user-friendly for those with basic SQL knowledge.</li>
                    <li>Use <strong>The Graph</strong> for very fast data retrieval focused on monitoring your dApp's events, states, and user interactions in real-time.</li>
                </ul>
            </>
        )
      },
      {
        question: 'What are its main capabilities?',
        answer: (
          <>
            <p className="mb-4">The engine provides a range of capabilities to protect and manage your blockchain operations, including:</p>
            <ul className="space-y-3 list-disc pl-5">
              <li>Monitoring all on-chain and off-chain interactions in real-time.</li>
              <li>Enforcing your company's pre-defined business rules and policies.</li>
              <li>Mitigating fraud, errors, and unauthorized actions.</li>
              <li>Providing security and compliance insights to stakeholders.</li>
            </ul>
          </>
        ),
      },
      {
        question: 'How does the engine handle transaction monitoring and security?',
        answer: 'FrontierBlox provides continuous oversight by monitoring all transactions on your connected blockchain networks and tracking interactions between nodes, APIs, and external systems. It identifies anomalies and rule violations as they happen and provides alerts for high-risk transactions, such as those involving flagged accounts or exceeding set thresholds. If a transaction violates a policy, it can be automatically suspended or rejected.',
      },
      {
        question: 'How does it help prevent fraud?',
        answer: 'The engine uses advanced methods like machine learning algorithms to detect patterns that could indicate fraud. It proactively prevents fraud by blocking transactions from flagged entities or geolocations and uses anomaly detection to identify unusual activities.',
      },
      {
        question: 'What about regulatory compliance?',
        answer: 'Compliance management is a core feature. The engine helps ensure that transactions meet jurisdictional requirements, including AML (Anti-Money Laundering), KYC (Know Your Customer), and GDPR. It automatically creates an unchangeable ledger of all transaction activities, which is essential for regulatory reporting and internal audits. It can also dynamically adjust rules to handle different regulations in multi-jurisdictional operations.',
      },
      {
        question: 'Does it work with different blockchains and my existing systems?',
        answer: "Yes. The engine has a blockchain-agnostic design and supports multiple protocols like Ethereum, Hyperledger, and Polkadot. It can also integrate with hybrid models that use both private and public blockchains. Furthermore, it is designed to interact with traditional financial systems through ZenithBlox's Universal Adapters.",
      },
      {
        question: 'What kind of reporting and analytics can I expect?',
        answer: 'FrontierBlox provides real-time dashboards that display live transaction data, compliance metrics, and system performance indicators. You can also generate customizable reports on-demand for stakeholders like regulators, auditors, and executives. The platform also offers predictive analytics to provide insights on potential compliance breaches or operational bottlenecks.',
      },
      {
        question: 'What are some practical use cases for the FrontierBlox Engine?',
        answer: (
          <>
            <p className="mb-4">The engine can be applied to various scenarios, including:</p>
            <ul className="space-y-3 list-disc pl-5">
              <li><strong>Cross-Border Payments:</strong> It monitors multi-currency transactions for anomalies, ensures adherence to exchange control regulations, and flags high-value transactions for extra scrutiny.</li>
              <li><strong>Asset Tokenization:</strong> It validates the tokenization process against specific regulations, monitors token transfers to prevent unauthorized movements, and provides detailed logs for regulators.</li>
              <li><strong>Institutional DeFi Adoption:</strong> It can enforce lending limits and collateralization ratios in DeFi protocols and identify suspicious borrowing or lending patterns.</li>
            </ul>
          </>
        ),
      },
      {
        question: 'What is the overall business impact of using this engine?',
        answer: (
          <>
            <p className="mb-4">The primary impacts on your business are:</p>
            <ul className="space-y-3 list-disc pl-5">
              <li><strong>Efficiency and Cost Savings:</strong> It automates compliance and risk management, which reduces the need for manual oversight and lowers operational costs.</li>
              <li><strong>Enhanced Security:</strong> It protects your operations against fraud, cyberattacks, and other risks, which fosters greater trust in your blockchain systems.</li>
              <li><strong>Regulatory Confidence:</strong> It provides the necessary tools to ensure your operations are transparent, compliant, and fully auditable.</li>
            </ul>
          </>
        ),
      },
    ],
  },
  {
    id: 'adapters',
    title: 'Universal Adapters',
    faqs: [
      {
        question: 'What are Universal Adapters and how do they help my business?',
        answer: 'The Universal Adapters are a core component of the ZenithBlox platform that enable your existing Web2 systems to work seamlessly with new Web3 blockchain networks. They are designed to handle all the technical complexities of blockchain integration, which allows your institution to save time and money, reduce integration costs, and focus on innovation and efficiency.',
      },
      {
        question: 'What are the main benefits of using Universal Adapters?',
        answer: (
          <>
            <p className="mb-4">The adapters are built to provide several key benefits:</p>
            <ul className="space-y-3 list-disc pl-5">
                <li><strong>Seamless Connection:</strong> They connect your traditional financial systems with blockchain networks without friction.</li>
                <li><strong>Data Compatibility:</strong> They help transform your data to ensure it works correctly with different systems.</li>
                <li><strong>Reduced Costs & Time:</strong> They significantly reduce the time-to-market and integration costs for businesses that are adopting blockchain technology.</li>
                <li><strong>Flexibility:</strong> They offer great flexibility by supporting many different blockchain protocols and legacy systems.</li>
            </ul>
          </>
        ),
      },
      {
        question: 'What types of systems can the Universal Adapters connect to?',
        answer: (
          <>
            <p className="mb-4">The adapters can connect to a wide range of existing Web2 systems. They come with pre-built connectors for common platforms, including:</p>
            <ul className="space-y-3 list-disc pl-5">
                <li><strong>Core Banking Systems:</strong> such as Swift, Temenos, and Finacle.</li>
                <li><strong>ERP Systems:</strong> such as SAP, Oracle, and Microsoft Dynamics.</li>
                <li><strong>CRM Platforms:</strong> such as Salesforce, HubSpot, and Zoho.</li>
            </ul>
            <p className="mt-4">They also have middleware capabilities to connect with your custom legacy systems.</p>
          </>
        ),
      },
      {
        question: 'Which blockchain networks and other services are supported?',
        answer: (
          <>
            <p className="mb-4">The Universal Adapters provide compatibility with a variety of popular public, private, and hybrid blockchain networks, as well as other essential services. Pre-configured adapters and components are available for:</p>
            <ul className="space-y-3 list-disc pl-5">
                <li><strong>Blockchains:</strong> Ethereum, Polkadot, Solana, Hyperledger Fabric, Binance Smart Chain, and Corda.</li>
                <li><strong>Financial Services:</strong> TradeTrust for secure electronic document exchange, Uniswap for DeFi token swaps, Stripe for payments, and Temenos for core banking operations.</li>
                <li><strong>Cloud & Databases:</strong> Microsoft Azure, Amazon Web Services (AWS), MySQL, Oracle DB, and PostgreSQL.</li>
                <li><strong>Business Systems:</strong> Salesforce for CRM, SAP for ERP, and Gmail for email communication.</li>
            </ul>
          </>
        ),
      },
      {
        question: 'How do the adapters ensure my data is secure and compliant?',
        answer: (
            <>
                <p className="mb-4">Security and compliance are core features of the Universal Adapters. They ensure secure and compliant operations by providing:</p>
                <ul className="space-y-3 list-disc pl-5">
                    <li>End-to-end encryption for all data, whether it is in transit or at rest.</li>
                    <li>Integration with AML/KYC platforms for automated compliance checks.</li>
                    <li>Real-time monitoring and detailed logging to create secure audit trails.</li>
                </ul>
            </>
        ),
      },
      {
        question: 'How can I customize the data flow and processing?',
        answer: (
            <>
                <p className="mb-4">The Universal Adapters include a variety of processors that allow you to control and manipulate the flow of information with no-code tools. These include the ability to:</p>
                <ul className="space-y-3 list-disc pl-5">
                    <li><strong>Combine and Route:</strong> Aggregate multiple messages into one, or route them based on "if/else" logic.</li>
                    <li><strong>Control Flow:</strong> Loop a process, delay it, or re-order messages as needed.</li>
                    <li><strong>Enrich Data:</strong> Add or modify information in a message by polling external sources or setting new data.</li>
                    <li><strong>Handle Errors:</strong> Use a "Circuit Breaker" to prevent system overloads from failing calls, and use "Do Catch" and "Rollback" features to handle exceptions and revert failed operations.</li>
                    <li><strong>Balance Load:</strong> Distribute processing across multiple endpoints to ensure high performance.</li>
                </ul>
            </>
        ),
      },
      {
        question: 'How do the Universal Adapters solve real-world business challenges?',
        answer: (
            <>
                <p className="mb-4">The adapters are designed for practical use cases to solve common challenges:</p>
                <ul className="space-y-3 list-disc pl-5">
                    <li><strong>Asset Tokenization:</strong> They can link your asset management systems directly to blockchain platforms, which automates the process of token issuance and tracking ownership.</li>
                    <li><strong>Cross-Border Payments:</strong> They connect traditional banking systems (like SWIFT) with blockchain networks to reduce the fees and processing times associated with cross-border payments.</li>
                    <li><strong>Supply Chain Management:</strong> They can connect your ERP systems to a blockchain to create a transparent and unchangeable record of all transactions and events in your supply chain.</li>
                </ul>
            </>
        ),
      },
      {
        question: 'What is the overall business impact of using Universal Adapters?',
        answer: (
            <>
                <p className="mb-4">By using the Universal Adapters, your business can achieve:</p>
                <ul className="space-y-3 list-disc pl-5">
                    <li><strong>Cost Efficiency:</strong> You can eliminate the need for expensive custom integrations, which reduces development time and costs.</li>
                    <li><strong>Operational Agility:</strong> You can simplify the process of adopting new technologies by bridging the gap between your current systems and the blockchain.</li>
                    <li><strong>Compliance Confidence:</strong> You can ensure you are adhering to regulations with automated checks and secure data handling.</li>
                    <li><strong>Accelerated Time-to-Market:</strong> You can deploy blockchain solutions much more rapidly, helping you gain a competitive edge in the market.</li>
                </ul>
            </>
        ),
      },
    ],
  },
];


const FaqItem = ({ faq, isOpen, onToggle }) => {
    return (
        <div className="py-5 border-b border-gray-200 last:border-b-0">
            <button
                onClick={onToggle}
                className="flex justify-between items-center w-full text-left"
            >
                <h3 className="text-base font-semibold text-gray-800">{faq.question}</h3>
                <div className="text-purple-600">
                    {isOpen ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 12.998H6V10.998H18V12.998Z" fill="currentColor"/></svg>
                    ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 12.998H13V17.998H11V12.998H6V10.998H11V5.99805H13V10.998H18V12.998Z" fill="currentColor"/></svg>
                    )}
                </div>
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: 'auto', marginTop: '16px' }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                        <div className="text-sm text-gray-600 leading-relaxed">{faq.answer}</div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const FaqSection = () => {
    const [activeTab, setActiveTab] = useState(faqData[0].id);
    const [openFaqIndex, setOpenFaqIndex] = useState(0);

    const activeTabData = faqData.find((tab) => tab.id === activeTab);

    const handleTabClick = (tabId) => {
        setActiveTab(tabId);
        setOpenFaqIndex(0); 
    };

    return (
        <div className="bg-black py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">

                {/* --- Title Section --- */}
                <AnimatedSection
                    triggerOnce={false}
                    animationVariants={fadeInUp}
                    className="text-center mb-10 md:mb-12"
                >
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
                        FAQs Related to Our Solutions
                    </h2>
                    <p className="mt-4 text-lg text-gray-400">
                        Find answers to common questions about our innovative modules.
                    </p>
                </AnimatedSection>
                {/* --- End Title Section --- */}

                <div className="bg-white rounded-2xl p-4 sm:p-6 md:p-10 lg:p-12 shadow-2xl">
                    {/* --- Buttons are now responsive --- */}
                    <div className="mb-8 flex flex-col sm:flex-row sm:flex-wrap justify-center items-center gap-4 sm:gap-6 md:gap-12">
                        {faqData.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => handleTabClick(tab.id)}
                                className={`w-full sm:w-auto px-5 py-2.5 text-sm font-semibold rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 ${
                                    activeTab === tab.id
                                        ? 'bg-purple-600 text-white shadow-md'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                {tab.title}
                            </button>
                        ))}
                    </div>

                    <div>
                        {activeTabData &&
                            activeTabData.faqs.map((faq, index) => (
                                <FaqItem
                                    key={index}
                                    faq={faq}
                                    isOpen={openFaqIndex === index}
                                    onToggle={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                                />
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FaqSection;